import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { request } from "../utils/api";
import { useBilling } from "../hooks/useBilling";
import useCurrentUser from "../hooks/useCurrentUser";
import { displayDateUS } from "../utils/dateUtils";
import {
  ArrowLeftIcon,
  DownloadIcon,
  PayrollIcon,
  PrintIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertCircleIcon,
  CloseIcon,
} from "../Icons";
import { openPrintWindow } from "../print/openPrintWindow";
import { buildInvoicePrintHtml } from "../print/forms/invoiceBillPrint";
import toast from "react-hot-toast";
import {
  SUBSCRIPTION_BTN,
  SUBSCRIPTION_PAGE,
  SUBSCRIPTION_PILL,
  SUBSCRIPTION_TABLE,
  SUBSCRIPTION_STATUS,
} from "./subscriptionTheme";
import { getInvoiceBreakdown, toMoneyNumber, isInformationalLine } from "../utils/invoiceBillingBreakdown";

/** Default company logo when tenant officeLogo is not set */
const DEFAULT_LOGO = "/login-logo.png";

/** Default company address for invoice */
const COMPANY_ADDRESS = "";

/** Footer contact info */
const INVOICE_FOOTER = {
  contact: "For any questions regarding this invoice, please contact the Finance Department.",
  checkPayable: "Please make checks payable to the company at the address above.",
};

const BillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { downloadInvoice, initiatePayment } = useBilling();
  const { officeLogo, tenant, user } = useCurrentUser();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const loadBillDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await request(`/billing/bills/${id}`);
      if (response && response.status === 1 && response.result) {
        // API returns { result: { bill: {...} } } - use nested bill when present
        const billData = response.result.bill ?? response.result;
        setBill(billData);
      } else {
        toast.error("Failed to load bill details");
        navigate("/bills");
      }
    } catch (err) {
      toast.error("Failed to load bill details");
      navigate("/bills");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadBillDetail();
  }, [loadBillDetail]);

  /** Pill colors come from the shared theme; only the icon is local. */
  const STATUS_ICON = {
    PAID: CheckCircleIcon,
    UNPAID: ClockIcon,
    OVERDUE: AlertCircleIcon,
    PARTIAL: ClockIcon,
    CANCELLED: CloseIcon,
    REFUNDED: CheckCircleIcon,
  };

  const getStatusConfig = (status) => {
    const tone = SUBSCRIPTION_STATUS[status] || SUBSCRIPTION_STATUS.UNPAID;
    return { ...tone, icon: STATUS_ICON[status] || ClockIcon };
  };

  const formatDate = (date) => displayDateUS(date) || "N/A";

  const getLogoUrl = () => {
    // if (officeLogo) {
    //   if (officeLogo.startsWith("http")) return officeLogo;
    //   return officeLogo.startsWith("/") ? officeLogo : `/${officeLogo}`;
    // }
    return DEFAULT_LOGO;
  };

  /** Absolute URL for print preview (tenant logo when available). */
  const getPrintLogoAbsoluteUrl = () => {
    const logo = officeLogo || tenant?.officeLogo || "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!logo || !String(logo).trim()) {
      return `${origin}${DEFAULT_LOGO}`;
    }
    if (logo.startsWith("http")) return logo;
    const path = logo.startsWith("/") ? logo : `/${logo}`;
    return `${origin}${path}`;
  };

  const getBillToName = () => {
    return tenant?.name || user?.name || "";
  };

  const getBillingAddress = () => {
    const fromTenant = [tenant?.address, tenant?.street, tenant?.city, tenant?.state, tenant?.zipCode].filter(Boolean).join(", ");
    if (fromTenant) return fromTenant;
    const fromUser = [user?.address, user?.street, user?.city, user?.state, user?.zipCode].filter(Boolean).join(", ");
    if (fromUser) return fromUser;
    return COMPANY_ADDRESS;
  };

  const handlePrintInvoice = () => {
    if (!bill) return;
    const html = buildInvoicePrintHtml(bill, {
      logoUrl: getPrintLogoAbsoluteUrl(),
      billToName: getBillToName(),
      billToAddress: getBillingAddress(),
    });
    if (!openPrintWindow(html)) {
      toast.error("Unable to open print window. Please allow pop-ups.");
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloadingInvoice(true);
    const result = await downloadInvoice(parseInt(id));
    if (result.success && result.data?.url) {
      window.open(result.data.url, "_blank");
      toast.success("Invoice downloaded successfully");
    } else {
      toast.error(result.message || "Failed to download invoice");
    }
    setDownloadingInvoice(false);
  };

  const handlePayNow = async () => {
    setProcessingPayment(true);
    try {
      const result = await initiatePayment(parseInt(id));
      if (result.success && result.data?.approvalUrl) {
        window.location.href = result.data.approvalUrl;
      } else {
        toast.error(result.message || "Failed to initiate payment");
        setProcessingPayment(false);
      }
    } catch (err) {
      toast.error("An error occurred while processing payment");
      setProcessingPayment(false);
    }
  };

  const isPaid = bill?.status === "PAID";
  const isPayable = ["UNPAID", "OVERDUE", "PARTIAL"].includes(bill?.status);

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center py-20">
        <div className="text-center">
          <div className={`${SUBSCRIPTION_PAGE.spinner} mx-auto`} />
          <p className="text-slate-600 mt-4 text-sm">Loading bill details...</p>
        </div>
      </div>
    );
  }

  if (!bill) return null;

  const statusCfg = getStatusConfig(bill.status);
  const StatusIcon = statusCfg.icon;
  
  const toNumber = toMoneyNumber;

  const {
    invoiceLines,
    invoiceSections,
    chargeSections,
    setupChargeAmount,
    computedLineSum,
  } = getInvoiceBreakdown(bill);
  /** Subtotal before discount/tax: prefer API, else line items (base + extra + one-time setup). */
  const rawDisplaySubtotal =
    bill.subtotal != null && bill.subtotal !== ""
      ? toNumber(bill.subtotal)
      : bill.lineSubtotal != null && bill.lineSubtotal !== ""
        ? toNumber(bill.lineSubtotal) + setupChargeAmount
        : computedLineSum;
  const displaySubtotal = Number.isFinite(rawDisplaySubtotal) ? rawDisplaySubtotal : computedLineSum;

  /** Discount from API (new shape) or legacy `discount` field. */
  const rawDiscountAmount = toNumber(bill.discountAmount ?? bill.discount);
  const discountAmount = Number.isFinite(rawDiscountAmount) ? rawDiscountAmount : 0;
  const hasDiscount = discountAmount > 0;

  const getDiscountLabel = () => {
    if (bill.discountReason && String(bill.discountReason).trim()) return bill.discountReason;
    const t = String(bill.discountType || "").toUpperCase();
    const v = toNumber(bill.discountValue);
    if (t === "PERCENT" || t === "PERCENTAGE") return `${v}% discount`;
    if (t === "FIXED" || t === "FLAT" || t === "AMOUNT") return `$${v.toFixed(2)} discount`;
    return "Discount";
  };

  const paidAmount = toNumber(bill.paidAmount);
  const balanceDue = toNumber(bill.totalAmount) - paidAmount;

  return (
    <div className="min-h-full w-full max-w-6xl mx-auto p-6 sm:p-8">
      {/* Invoice Card - Professional, Classical Layout */}
      <div className={SUBSCRIPTION_PAGE.card}>
        {/* Header: Logo (left) | Invoice Details (right) - Compact */}
        <div className={`px-6 sm:px-8 py-4 border-b ${SUBSCRIPTION_PAGE.divider}`}>
          <div className="flex flex-row items-center justify-between gap-6">
            <img
              src={getLogoUrl()}
              alt="Company"
              className="h-28 sm:h-32 w-auto max-w-[280px] object-contain flex-shrink-0"
              onError={(e) => {
                e.target.src = DEFAULT_LOGO;
              }}
            />
            <div className="text-right flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-indigo-900 tracking-tight">INVOICE</h1>
              <div className="mt-2 space-y-1 text-sm">
                <p><span className="font-semibold text-indigo-700">INVOICE #</span> <span className="text-slate-900">{bill.billNumber}</span></p>
                {/* <p><span className="font-semibold text-indigo-700">DATE</span> <span className="text-slate-900">{getInvoiceDate()}</span></p> */}
                <p><span className="font-semibold text-indigo-700">DUE DATE</span> <span className="text-slate-900">{formatDate(bill.dueDate)}</span></p>
                {/* <p><span className="font-semibold text-indigo-700">TERMS</span> <span className="text-slate-900">{getTerms()}</span></p> */}
              </div>
              <span className={`${SUBSCRIPTION_PILL} mt-2 ${statusCfg.className}`}>
                <StatusIcon className="w-3 h-3" />
                {statusCfg.label}
              </span>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className={`px-8 sm:px-10 py-5 border-b ${SUBSCRIPTION_PAGE.divider}`}>
          <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-2">Bill To</p>
          <p className="text-sm text-slate-800 leading-relaxed">{getBillToName()}</p>
          <p className="text-sm text-slate-800 leading-relaxed">{getBillingAddress()}</p>
        </div>

        {/* Billing Period */}  
        <div className={`px-8 sm:px-10 py-4 border-b ${SUBSCRIPTION_PAGE.band}`}>
          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Billing Period</p>
          <p className="text-sm text-slate-800 mt-1">
            {formatDate(bill.billingPeriodStart)} – {formatDate(bill.billingPeriodEnd)}
          </p>
        </div>

        {chargeSections.length > 0 ? (
          <div className={`px-8 sm:px-10 py-4 border-b ${SUBSCRIPTION_PAGE.divider}`}>
            <div className={`grid gap-3 ${chargeSections.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}>
              {chargeSections.map((section) => (
                <div
                  key={section.key}
                  className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-4 py-3"
                >
                  <p className="text-xs font-bold uppercase tracking-wider text-indigo-700">
                    {section.title}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-800">
                    Basic plan: {section.included} included · ${toNumber(section.baseAmount).toFixed(2)}
                  </p>
                  <p className="mt-1 text-sm text-slate-800">
                    {section.actual} billable
                    {section.extra > 0
                      ? ` · ${section.extra} extra × $${toNumber(section.unitPrice).toFixed(2)} = $${toNumber(section.extraAmount).toFixed(2)}`
                      : " · no extras"}
                  </p>
                  <p className="mt-1 text-sm font-semibold tabular-nums text-slate-900">
                    {section.title} total: ${toNumber(section.sectionTotal ?? (toNumber(section.baseAmount) + toNumber(section.extraAmount))).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Activity Table - grouped employee / patient / setup */}
        <div className="p-8 sm:p-10">
          <div className="border border-indigo-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className={SUBSCRIPTION_TABLE.headRow}>
                  <th className={`${SUBSCRIPTION_TABLE.headCell} w-[46%]`}>Description</th>
                  <th className={`${SUBSCRIPTION_TABLE.headCellRight} w-[14%]`}>Qty</th>
                  <th className={`${SUBSCRIPTION_TABLE.headCellRight} w-[20%]`}>Rate</th>
                  <th className={`${SUBSCRIPTION_TABLE.headCellRight} w-[20%]`}>Amount</th>
                </tr>
              </thead>
              <tbody className={SUBSCRIPTION_TABLE.body}>
                {(invoiceSections.length > 0 ? invoiceSections : [{ section: "shared", title: "", lines: invoiceLines }]).map((group) => (
                  <React.Fragment key={group.section || group.title}>
                    {invoiceSections.length > 1 && group.title ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-2 bg-indigo-50/70 text-[11px] font-bold uppercase tracking-wider text-indigo-700"
                        >
                          {group.title}
                        </td>
                      </tr>
                    ) : null}
                    {(group.lines || []).map((line) => {
                      const info = isInformationalLine(line);
                      return (
                        <tr key={line.key || line.name}>
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-900">{line.name}</p>
                            {line.description ? (
                              <p className="text-xs text-gray-500 mt-0.5">{line.description}</p>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-slate-700">
                            {line.quantity == null ? "—" : line.quantity}
                          </td>
                          <td className="px-6 py-4 text-right tabular-nums text-slate-700">
                            {line.rate == null ? "—" : `$${toNumber(line.rate).toFixed(2)}`}
                          </td>
                          <td className="px-6 py-4 text-right font-medium tabular-nums">
                            {info ? "—" : `$${toNumber(line.amount).toFixed(2)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals - Symmetrical */}
          <div className="mt-8 flex justify-end">
            <div className="w-full max-w-[320px] space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium text-slate-900 tabular-nums">${displaySubtotal.toFixed(2)}</span>
              </div>
              {toNumber(bill.tax) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Tax</span>
                  <span className="font-medium text-slate-900 tabular-nums">${toNumber(bill.tax).toFixed(2)}</span>
                </div>
              )}
              {hasDiscount && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span className="pr-2">{getDiscountLabel()}</span>
                  <span className="font-medium tabular-nums shrink-0">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              {paidAmount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Paid</span>
                  <span className="font-medium tabular-nums">${paidAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline gap-4 pt-3 mt-2 border-t border-indigo-200">
                <span className="font-bold text-indigo-900">{isPaid ? "Total Paid" : "Balance Due"}</span>
                <span className="text-2xl font-bold text-indigo-700 tabular-nums">
                  USD ${isPaid ? toNumber(bill.totalAmount).toFixed(2) : balanceDue.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Contact & Check instructions */}
        <div className={`px-8 sm:px-10 py-5 border-t ${SUBSCRIPTION_PAGE.band}`}>
          <p className="text-xs text-slate-600">{INVOICE_FOOTER.contact}</p>
          {/* <p className="text-xs text-gray-600 mt-2">{INVOICE_FOOTER.checkPayable}</p> */}
        </div>

        {/* Payment History */}
        {/* {bill.payments && bill.payments.length > 0 && (
          <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Payment History</h2>
            <div className="border border-gray-200 rounded overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-[25%]">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-[20%]">Method</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase w-[20%]">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-[35%]">Transaction ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bill.payments.map((p) => (
                    <tr key={p.id}>
                      <td className="px-6 py-3 text-gray-700">{formatDateTime(p.initiatedAt || p.createdAt)}</td>
                      <td className="px-6 py-3 text-gray-700">{p.paymentGateway || p.gateway || "PayPal"}</td>
                      <td className="px-6 py-3 text-right font-medium tabular-nums">${toNumber(p.amount).toFixed(2)}</td>
                      <td className="px-6 py-3 text-gray-500">{p.transactionNumber || p.transactionId || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )} */}

        {/* Action Buttons: Bottom row - Cancel/Back (left) | Pay Now/Download (right) */}
        <div className={`px-8 py-4 border-t flex flex-wrap items-center justify-between gap-3 ${SUBSCRIPTION_PAGE.band}`}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/bills")}
              className={SUBSCRIPTION_BTN.outline}
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to Bills
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handlePrintInvoice}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium rounded-lg transition-colors"
            >
              <PrintIcon className="w-4 h-4" />
              Print
            </button>
            {isPayable ? (
              <button
                onClick={handlePayNow}
                disabled={processingPayment}
                className={
                  bill.status === "OVERDUE"
                    ? SUBSCRIPTION_BTN.danger
                    : SUBSCRIPTION_BTN.primary
                }
              >
                <PayrollIcon className="w-4 h-4" />
                {processingPayment ? "Processing..." : "Pay Now"}
              </button>
            ) : (
              <button
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
                className={SUBSCRIPTION_BTN.primary}
              >
                <DownloadIcon className="w-4 h-4" />
                {downloadingInvoice ? "Downloading..." : "Download PDF"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillDetail;
